import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import { EventInvitationMessage } from 'Src/models/message';
import { SchoolEvent } from 'Src/models/event';
import { QUESTIONS_TEMPLATE_TYPE } from 'Src/consts/message';
import { getParentConsentEventName } from '../getParentConsentEventName';
import {
  getLastResponseUpdatedAtText,
  getRespondedAtText,
  getResponseUser,
  statusChangedAfterFirstResponse,
  wasEditedAfterResponse
} from 'Src/helpers/message/message';
import { FormAnswersSummary } from 'Src/components/FormAnswersSummary/FormAnswersSummary';
import { ConsentRequestHistory } from 'Src/components/ConsentRequestHistory/ConsentRequestHistory';
import { answersToMap } from 'Src/helpers/form/formRuntime';
import './ConsentRequestDetails.scss';

interface Props {
  message: {
    consentRequest: EventInvitationMessage;
    childEvent: SchoolEvent;
  };
  childIdList?: string[];
}

export const ConsentRequestDetails: React.FC<Props> = ({ message, childIdList = [] }) => {
  if (!message) return null;

  const { consentRequest, childEvent } = message;
  const schoolName = propz.get(consentRequest, ['schoolData', 'name'], '');
  const eventName = getParentConsentEventName(childEvent, childIdList, consentRequest);

  const firstName = propz.get(consentRequest, ['playerDetailsData', 'firstName'], '');
  const lastName = propz.get(consentRequest, ['playerDetailsData', 'lastName'], '');
  const studentName = `${firstName} ${lastName}`.trim() || 'Student not specified';

  const startTime = propz.get(childEvent, ['startTime']);
  const endTime = propz.get(childEvent, ['endTime']);
  const start = startTime ? moment(startTime) : null;
  const end = endTime ? moment(endTime) : null;
  const sameDay = !!start && !!end && start.isValid() && end.isValid() && start.isSame(end, 'day');
  const dateText = start?.isValid()
    ? sameDay
      ? `${start.format('ddd D MMM YYYY')} · ${start.format('HH:mm')}–${end!.format('HH:mm')}`
      : end?.isValid()
      ? `${start.format('ddd D MMM YYYY, HH:mm')} – ${end.format('ddd D MMM YYYY, HH:mm')}`
      : start.format('ddd D MMM YYYY, HH:mm')
    : '';

  const placeName = propz.get(childEvent, ['venue', 'placeData', 'name'], '');
  const postcode = propz.get(childEvent, ['venue', 'postcodeData', 'postcode'], '');
  const venueType = propz.get(childEvent, ['venue', 'venueType'], '');
  const venue = [placeName || venueType, postcode].filter(Boolean).join(', ');
  const deadline = propz.get(consentRequest, ['deadlineForAnswers'], '');
  const deadlineText = deadline && moment(deadline).isValid() ? moment(deadline).format('D MMM YYYY, HH:mm') : '';

  const rawStatus = String(propz.get(consentRequest, ['invitationStatus'], '') || '').toUpperCase();
  const isOverdue = !!deadline && new Date(deadline) <= new Date();
  const eventStarted = !!start && start.isValid() && moment().isSameOrAfter(start);
  let statusClass = 'is-awaiting';
  let statusText = 'Waiting for response';

  if (rawStatus === 'ACCEPTED') {
    statusClass = 'is-accepted';
    statusText = 'Consent given';
  } else if (rawStatus === 'REJECTED') {
    statusClass = 'is-declined';
    statusText = 'Consent declined';
  } else if (rawStatus === 'OUTDATED' || isOverdue || (!deadline && eventStarted)) {
    statusClass = 'is-overdue';
    statusText =
      rawStatus === 'OUTDATED' ? 'Outdated request' : isOverdue ? 'Response deadline passed' : 'Event started';
  }

  const respondedAt = getRespondedAtText(consentRequest);
  const lastChangedAt = getLastResponseUpdatedAtText(consentRequest);
  const responseUser = getResponseUser(consentRequest);
  const wasUpdated = wasEditedAfterResponse(consentRequest) || statusChangedAfterFirstResponse(consentRequest);
  const fields = Array.isArray(consentRequest.fields) ? consentRequest.fields : [];
  const hasLegacyAnswers = fields.some((field: any) => typeof field?.value !== 'undefined');
  const hasFormAnswers = Array.isArray(consentRequest.formAnswers) && consentRequest.formAnswers.length > 0;
  const hasResponse =
    rawStatus === 'ACCEPTED' ||
    rawStatus === 'REJECTED' ||
    !!consentRequest.isActionPerformed ||
    !!respondedAt ||
    !!responseUser.fullName ||
    !!responseUser.email ||
    hasLegacyAnswers ||
    hasFormAnswers;
  const initials = (responseUser.fullName || responseUser.email || studentName)
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');

  const renderLegacyAnswer = (field: any): React.ReactNode => {
    const { value, type } = field || {};
    if (typeof value === 'undefined' || value === null || value === '') return '—';
    if (type === QUESTIONS_TEMPLATE_TYPE.BOOLEAN) return value === true || value === 'true' ? 'Yes' : 'No';
    return Array.isArray(value) ? value.join(', ') : String(value);
  };

  return (
    <div className="consentDetails">
      <section className="consentDetails__hero">
        <div className="consentDetails__icon" aria-hidden>
          <span />
        </div>
        <div className="consentDetails__heroText">
          <span className="consentDetails__eyebrow">Consent request</span>
          <h3>{studentName}</h3>
          <p>{eventName}{dateText ? ` · ${dateText}` : ''}</p>
          <span className={`consentDetails__status ${statusClass}`}>{statusText}</span>
        </div>
      </section>

      {deadlineText ? <div className="consentDetails__deadline">Reply by <strong>{deadlineText}</strong></div> : null}

      <section className="consentDetails__section consentDetails__response">
        {consentRequest.updatedByStaff ? <p className="consentDetails__deadline" role="note">
          <strong>Updated by staff</strong> — A member of school staff changed this consent request.
          {consentRequest.staffUpdatedAt ? ` ${moment(consentRequest.staffUpdatedAt).format('D MMM YYYY, HH:mm')}.` : ''}
        </p> : null}
        <div className="consentDetails__sectionHead">
          <h4>{rawStatus === 'ACCEPTED' ? 'Consent given' : rawStatus === 'REJECTED' ? 'Declined' : 'Your response'}</h4>
          {wasUpdated ? <span className="consentDetails__edited">Edited response</span> : null}
        </div>

        {hasResponse ? (
          <>
            {rawStatus === 'REJECTED' && consentRequest.declineReason ? <blockquote className="consentDetails__declineReason">“{consentRequest.declineReason}”</blockquote> : null}
            {responseUser.fullName || responseUser.email ? (
              <div className="consentDetails__responder">
                <span className="consentDetails__avatar" aria-hidden>
                  {initials}
                </span>
                <div>
                  <span>Responded by</span>
                  {responseUser.fullName ? <strong>{responseUser.fullName}</strong> : null}
                  {responseUser.email ? <small>{responseUser.email}</small> : null}
                </div>
              </div>
            ) : null}

            {respondedAt || (wasUpdated && lastChangedAt) ? (
              <dl className="consentDetails__responseDates">
                {respondedAt ? (
                  <div>
                    <dt>Responded at</dt>
                    <dd>{respondedAt}</dd>
                  </div>
                ) : null}
                {wasUpdated && lastChangedAt ? (
                  <div>
                    <dt>Last changed</dt>
                    <dd>{lastChangedAt}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}

            <div className="consentDetails__answers">
              <h5>{consentRequest.updatedByStaff ? 'Recorded answers' : 'Your answers'}</h5>
              {consentRequest.formDefinition ? (
                <FormAnswersSummary
                  definition={consentRequest.formDefinition}
                  answers={answersToMap(consentRequest.formAnswers || [])}
                  emptyText="No additional questions were included with this request."
                />
              ) : fields.length ? (
                <dl className="consentDetails__answerList">
                  {fields.map((field: any, index: number) => (
                    <div key={`${field.heading || 'question'}_${index}`}>
                      <dt>{field.heading || `Question ${index + 1}`}</dt>
                      <dd>{renderLegacyAnswer(field)}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <div className="consentDetails__empty">No additional questions were included with this request.</div>
              )}
            </div>
          </>
        ) : (
          <div className="consentDetails__empty">No response has been submitted yet.</div>
        )}
      </section>
      <section className="consentDetails__section"><h4>History</h4><ConsentRequestHistory message={consentRequest}/></section>
    </div>
  );
};

ConsentRequestDetails.displayName = 'ConsentRequestDetails';
